import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { primary } from '../../assets/colors'

type SelectionRect = { x: number; y: number; w: number; h: number }

const MIN_CROP_PX = 5

function cropCanvasToBlob(sourceCanvas: HTMLCanvasElement, rect: SelectionRect): Promise<Blob> {
    const { x, y, w, h } = rect
    const cropped = document.createElement('canvas')
    cropped.width = Math.max(1, Math.round(w))
    cropped.height = Math.max(1, Math.round(h))
    const ctx = cropped.getContext('2d')
    if (!ctx) return Promise.reject(new Error('Canvas context not available'))
    ctx.drawImage(sourceCanvas, Math.max(0, x), Math.max(0, y), cropped.width, cropped.height, 0, 0, cropped.width, cropped.height)
    return new Promise((resolve, reject) => {
        cropped.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))), 'image/png')
    })
}

type Props = {
    canvas: HTMLCanvasElement
    onDone: (blob: Blob) => void
    onCancel: () => void
}

/** Full-screen review of a captured frame: drag to crop, Enter to keep, Esc to discard. */
export function ScreenshotEditorOverlay({ canvas, onDone, onCancel }: Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const displayCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null)
    const selectionStart = useRef<{ x: number; y: number } | null>(null)

    const getCoords = useCallback(
        (clientX: number, clientY: number): { x: number; y: number } | null => {
            if (!containerRef.current) return null
            const rect = containerRef.current.getBoundingClientRect()
            return {
                x: (clientX - rect.left) * (canvas.width / rect.width),
                y: (clientY - rect.top) * (canvas.height / rect.height)
            }
        },
        [canvas]
    )

    useEffect(() => {
        const display = displayCanvasRef.current
        if (!display) return
        display.width = canvas.width
        display.height = canvas.height
        const ctx = display.getContext('2d')
        if (!ctx) return
        ctx.clearRect(0, 0, display.width, display.height)
        ctx.drawImage(canvas, 0, 0)
    }, [canvas])

    const hasCrop = !!selectionRect && selectionRect.w >= MIN_CROP_PX && selectionRect.h >= MIN_CROP_PX

    const handleDone = useCallback(async () => {
        const rect = hasCrop && selectionRect ? selectionRect : { x: 0, y: 0, w: canvas.width, h: canvas.height }
        onDone(await cropCanvasToBlob(canvas, rect))
    }, [canvas, hasCrop, onDone, selectionRect])

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!selectionStart.current) return
            const coords = getCoords(e.clientX, e.clientY)
            if (!coords) return
            setSelectionRect({
                x: Math.min(selectionStart.current.x, coords.x),
                y: Math.min(selectionStart.current.y, coords.y),
                w: Math.abs(coords.x - selectionStart.current.x),
                h: Math.abs(coords.y - selectionStart.current.y)
            })
        }
        const onMouseUp = () => {
            selectionStart.current = null
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                onCancel()
            } else if (e.key === 'Enter') {
                e.preventDefault()
                void handleDone()
            }
        }
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
        window.addEventListener('keydown', onKeyDown, true)
        return () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
            window.removeEventListener('keydown', onKeyDown, true)
        }
    }, [getCoords, handleDone, onCancel])

    const handleCanvasMouseDown = useCallback(
        (e: React.MouseEvent) => {
            const coords = getCoords(e.clientX, e.clientY)
            if (!coords) return
            selectionStart.current = coords
            setSelectionRect({ x: coords.x, y: coords.y, w: 0, h: 0 })
        },
        [getCoords]
    )

    return (
        <Box
            role="dialog"
            aria-label="Review screenshot"
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 10002,
                bgcolor: 'rgba(0,0,0,0.65)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Paper
                elevation={0}
                sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 1.5,
                    maxWidth: '92vw',
                    maxHeight: '92vh',
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.palette.mode === 'dark' ? '0 24px 64px rgba(0,0,0,0.6)' : theme.shadows[12]
                })}
            >
                <Box
                    ref={containerRef}
                    onMouseDown={handleCanvasMouseDown}
                    sx={{
                        position: 'relative',
                        cursor: 'crosshair',
                        borderRadius: 1,
                        overflow: 'hidden',
                        flex: '1 1 auto',
                        minHeight: 0,
                        lineHeight: 0,
                        border: 1,
                        borderColor: 'divider'
                    }}
                >
                    <canvas
                        ref={displayCanvasRef}
                        width={canvas.width}
                        height={canvas.height}
                        style={{ maxWidth: '100%', maxHeight: 'calc(92vh - 96px)', display: 'block' }}
                    />
                    {selectionRect && selectionRect.w > 0 && selectionRect.h > 0 ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: `${(selectionRect.x / canvas.width) * 100}%`,
                                top: `${(selectionRect.y / canvas.height) * 100}%`,
                                width: `${(selectionRect.w / canvas.width) * 100}%`,
                                height: `${(selectionRect.h / canvas.height) * 100}%`,
                                border: `1.5px solid ${primary}`,
                                bgcolor: alpha(primary, 0.15),
                                boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                                pointerEvents: 'none'
                            }}
                        />
                    ) : null}
                </Box>

                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                        {hasCrop ? 'Only the selected area will be attached.' : 'Drag to crop, or keep the whole screen.'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {hasCrop ? (
                            <Button variant="text" color="inherit" size="small" onClick={() => setSelectionRect(null)} sx={{ height: 30, color: 'text.secondary' }}>
                                Reset
                            </Button>
                        ) : null}
                        <Button variant="outlined" color="inherit" size="small" onClick={onCancel} sx={{ height: 30 }}>
                            Discard
                        </Button>
                        <Button variant="contained" size="small" disableElevation onClick={handleDone} sx={{ height: 30 }}>
                            Attach
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    )
}
